import _ from 'lodash'
import { Entity } from '../../entity'
import { Store } from '../../store'
import { evaluateExpressionWithValueGetter } from '../../../helpers/evaluation'
import { Formatter } from '../../../helpers/formatter'
import { CustomFormulas } from '../../../helpers/formulas'
import { Event } from './storyboard-execution-model'
import { jsonPatchToObject } from './storyboard-utils'

type EventToContentHandler = (event: Event) => any

/**
 * Get the entity id that associated with this event
 * @param event
 */
export const getEventEntityId = (event: Event) => {
  return _.get(event, ['associations', 0, 'entityId'])
}

/**
 * Map 'core_storyboard_event_changeSet' to entity json
 *
 * @param event a change set event
 */
const changeSetEventHandler = (event: Event): any => {
  const patch = _.get(event, 'details.patch', [])
  const content = jsonPatchToObject(patch) || {}
  const prevContent = jsonPatchToObject(patch, 'prevValue') || {}
  const entityId = getEventEntityId(event)
  return {
    uniqueId: entityId,
    _prevContent: {
      uniqueId: entityId,
      ...prevContent,
    },
    _changeSetContent: {
      uniqueId: entityId,
      ...content,
    },
  }
}

/**
 * Map 'core_yms_event_trailer_snapshot' to entity json
 */
const snapshotEventHandler = (event: Event): any => {
  const content = _.get(event, 'details', {})
  return {
    uniqueId: getEventEntityId(event),
    ...content,
  }
}

/**
 * default map handler to map event to entity content
 */
const defaultEventContentHandler: Record<string, EventToContentHandler> = {
  core_yms_event_trailer_snapshot: snapshotEventHandler,
  core_storyboard_event_changeSet: changeSetEventHandler,
}

/**
 * Map the events in the execution to json entity data blob.  This is used to map
 * certain event like snapshot and changeset for export purpose.  The entity blob will
 * have these fields
 * {
 *    uniqueId: entity id
 *    _prevContent: The content before the user's modification.
 *    _changeSetContent: The content after the user's modification.
 *    <content>: The content at the time of the snapshot.
 * }
 */
const buildBlobs = (
  entity: Entity,
  handlers: Record<string, EventToContentHandler>
): Map<string, any> => {
  const events = entity.get('core_storyboard_execution.events', [])

  return _.reduce(
    events,
    (rows, event) => {
      const handler = handlers[event.eventType]
      if (_.isNil(handler)) {
        return rows
      }

      const entityId = getEventEntityId(event)
      const data =
        rows.get(entityId) ??
        rows
          .set(entityId, { uniqueId: entityId, _changeSetContent: {}, _prevContent: {} })
          .get(entityId)

      _.merge(data, handler(event))

      return rows
    },
    new Map<string, any>()
  )
}

/** An edge recorded as just an id, e.g. a spot prefilled with ENTITY_EDGE, so it has no name. */
const isUnnamedEdge = (value: any): boolean =>
  _.isPlainObject(value) && _.isString(value.entityId) && _.isEmpty(value.displayName)

const collectUnnamedEdges = (value: any, edges: any[] = []): any[] => {
  if (isUnnamedEdge(value)) {
    edges.push(value)
  }
  if (_.isObjectLike(value)) {
    _.forEach(value, (child) => collectUnnamedEdges(child, edges))
  }
  return edges
}

/**
 * The server-computed name if present, else the schema `formatString`. Not Entity.displayName,
 * which prefers `clientFormatString` and can mix in live state an audit's history shouldn't show.
 */
const edgeDisplayName = (record: Entity | undefined): string | undefined => {
  const precomputedName = _.get(record?.content, 'precomputation.displayName')
  if (!_.isEmpty(precomputedName)) {
    return precomputedName
  }
  const schema = _.findLast(
    record?.schemas,
    (schema) => !_.isEmpty(schema?.get('metadata.displayName'))
  )
  const formatString = schema?.get('metadata.displayName.formatString')
  return _.isEmpty(formatString)
    ? record?.displayName
    : Formatter.formatWithTwig(formatString, record?.content)
}

/** Sets the displayName of every edge whose record is already cached. */
const fillNamesFromCache = (edges: any[], store: Store): void => {
  _.forEach(edges, (edge) => {
    const displayName = edgeDisplayName(store.getRecord(edge.entityId))
    if (!_.isEmpty(displayName)) {
      edge.displayName = displayName
    }
  })
}

const unnamedIds = (edges: any[]): string[] =>
  _.uniq(_.map(_.reject(edges, 'displayName'), 'entityId'))

/**
 * Name every unnamed edge in the blobs. Mutates in place, which is safe only because these blobs
 * are scaffolding — never do this to a store-owned entity's content. Cached names land before this
 * returns; a failed fetch is swallowed, leaving those names unset.
 */
const resolveEdgeDisplayNames = async (blobs: Map<string, any>, store: Store): Promise<void> => {
  try {
    const edges = collectUnnamedEdges([...blobs.values()])
    // before the first await, so cached names land synchronously for the caller
    fillNamesFromCache(edges, store)
    const missingIds = unnamedIds(edges)
    if (_.isEmpty(missingIds)) {
      return
    }
    // not getOrFetchRecords: it keeps a rejected promise per id and so never retries
    await store.findRecords(missingIds, { shouldCacheRecord: true })
    fillNamesFromCache(edges, store)
  } catch (e: any) {
    console.warn(`[yms-utils] failed to resolve edge display names: ${e?.message}`)
  }
}

/**
 * Entity blobs for the execution's events, carrying the edge displayNames the store already had.
 * The rest are fetched and cached in the background, so they appear on the next render; nothing
 * re-renders the table when they arrive.
 */
export const eventsToEntityBlobs = (
  entity: Entity,
  handlers: Record<string, EventToContentHandler> = defaultEventContentHandler
): Map<string, any> => {
  const blobs = buildBlobs(entity, handlers)
  const store = entity.getStore?.()
  if (store) {
    void resolveEdgeDisplayNames(blobs, store)
  }
  return blobs
}

/**
 * {@link eventsToEntityBlobs} for callers that cannot re-render when a name arrives later: waits
 * for the edge displayNames that need a fetch before returning.
 */
export const fetchEventEntityBlobs = async (
  entity: Entity,
  handlers: Record<string, EventToContentHandler> = defaultEventContentHandler
): Promise<Map<string, any>> => {
  const blobs = buildBlobs(entity, handlers)
  const store = entity.getStore?.()
  if (store) {
    await resolveEdgeDisplayNames(blobs, store)
  }
  return blobs
}

/**
 * Map the execution events to ES query results for the entity table.
 */
export const eventsToESQueryResult = (entity: Entity) => {
  return _.map([...eventsToEntityBlobs(entity).values()], (blob) => {
    return {
      data: blob,
      children: [],
      metadata: { totalEntityMatchCount: 1 },
    }
  })
}

export const entityBlobsToRows = async (
  entities: Map<string, any>,
  columns: any[],
  context: any = {}
): Promise<string[][]> => {
  const rows: string[][] = []

  const headers = getColumnHeaders(columns)
  rows.push(headers)

  for (const [key, value] of entities.entries()) {
    const row = await entityBlobToRow(value, columns, context)
    rows.push(row)
  }

  return rows
}

export const entityBlobToRow = async (
  entity = {},
  columns: any[],
  context = {}
): Promise<string[]> => {
  const localContext = {
    _,
    ...context,
    ...entity,
  }
  const valueGetter = (key: string) => _.get(localContext, key, CustomFormulas[key])

  const row: string[] = []

  for (const entry of columns) {
    const { formula, path } = entry
    try {
      // look for the current content first, then fallback to snapshot, then formula
      const evaluated =
        _.get(localContext, path) ??
        _.get(localContext, '_changeSetContent.' + path) ??
        evaluateExpressionWithValueGetter(formula, valueGetter)
      const value = _.isNil(evaluated?.then) ? evaluated : await Promise.resolve(evaluated)
      row.push(value ?? '')
    } catch (e: any) {
      // for any error, set the column to empty
      row.push('')
    }
  }

  return row
}

export const getColumnHeaders = (columns: any[]): string[] => {
  return columns.map((item) => item.label)
}
